import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import {
  Box, Typography, TextField, Button, Divider, CircularProgress,
  Switch, FormControlLabel, InputAdornment,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LabelIcon from '@mui/icons-material/Label';
import CloudIcon from '@mui/icons-material/Cloud';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

// ── Design tokens (matches Home.js) ──────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
};

// ── Reusable compact text field ───────────────────────────────────────────────
const Field = ({ label, name, value, onChange, placeholder, type = 'text',
  maxLength, pattern, required, align, startIcon, multiline, rows }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
    </Typography>
    <TextField
      size="small"
      name={name}
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder || label}
      type={type}
      required={required}
      multiline={multiline}
      rows={rows}
      inputProps={{ maxLength, pattern, style: { fontSize: '12px', textAlign: align || 'left', padding: '5px 8px' } }}
      InputProps={startIcon ? { startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment> } : undefined}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '7px',
          bgcolor: '#fafafa',
          '& fieldset': { borderColor: C.cardBorder },
          '&:hover fieldset': { borderColor: C.accentBorder },
          '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
        },
      }}
    />
  </Box>
);

// ── Section card wrapper ──────────────────────────────────────────────────────
const Section = ({ title, icon, children, color = C.accent }) => (
  <Box sx={{
    bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow,
    border: `1px solid ${C.cardBorder}`, overflow: 'hidden',
  }}>
    {/* Section header */}
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.8,
      px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`,
      bgcolor: `${color}0d`,
    }}>
      <Box sx={{ color, fontSize: 15, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
      {children}
    </Box>
  </Box>
);

// ── Toggle field ──────────────────────────────────────────────────────────────
const ToggleField = ({ label, name, checked, onChange }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    px: '10px', py: '5px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`,
    bgcolor: '#fafafa', minWidth: 140,
  }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: C.text }}>{label}</Typography>
    <Switch
      size="small"
      name={name}
      checked={!!checked}
      onChange={onChange}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: C.accent },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: C.accent },
      }}
    />
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const CompanyEdit = ({ match }) => {
  const history = useHistory();
  const { isAuthenticated, companyId, isSuperAdmin, logout, token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); }
  }, [isAuthenticated, token, logout]);

  const [formData, setFormData] = useState({
    Name: '', ContactNumber1: '', ContactNumber2: '', Email: '',
    AddressLine1: '', AddressLine2: '', GSTNumber: '', PanNumber: '',
    Location: '', PinCode: '', CashOpening: '', StockOpening: '',
    PaymentUPIId: '', PaymentUPIName: '',
    BToBPrefix: '', BToCPrefix: '', IGSTPrefix: '', IGSTCustomerPrefix: '',
    FreightGSTRate: '', IsEinvoice: false, IsEwayBill: false,
    EinvoicePortalUserName: '', EinvoicePortalPassword: '',
    myGSTcafeCustomerID: '', myGSTcafeApiID: '', myGSTcafeApiSecret: '',
  });

  useEffect(() => {
    fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)
      .then(res => { if (res.responseCode === '1111') setFormData(res.response_data); })
      .catch(err => console.error('Error fetching company:', err));
  }, [companyId, token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value === 'true' ? true : value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await updateData('CompanyApi', `${companyId}`, { ...formData, UpdatedBy: 1 }, token);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Error updating company:', err);
    } finally {
      setSaving(false);
    }
  };

  const phonePattern = /^\d{10}$/.source;
  const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/.source;

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <BusinessIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Company Settings
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          <Button
            component={Link} to="/companyIndex"
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 600, textTransform: 'none',
              color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: '7px', px: 1.2, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
            }}
          >
            Back
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            size="small"
            startIcon={saving ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 13 }} />}
            sx={{
              fontSize: '11px', fontWeight: 700, textTransform: 'none',
              bgcolor: saved ? '#22c55e' : C.accent, color: '#fff',
              borderRadius: '7px', px: 1.4, py: 0.4, boxShadow: C.shadow,
              '&:hover': { bgcolor: C.accentDark },
              transition: 'background 0.3s',
            }}
          >
            {saved ? 'Saved!' : 'Update'}
          </Button>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>

          {/* ── Basic Info ── */}
          <Section title="Basic Information" icon={<BusinessIcon />}>
            <Box sx={{ flex: '1 1 100%' }}>
              <Field label="Company Name" name="Name" value={formData.Name}
                onChange={handleChange} required maxLength={50} />
            </Box>
            <Box sx={{ flex: '1 1 160px' }}>
              <Field label="Contact Number 1" name="ContactNumber1" value={formData.ContactNumber1}
                onChange={handleChange} required maxLength={15} pattern={phonePattern}
                startIcon={<PhoneIcon sx={{ fontSize: 13, color: C.muted }} />} />
            </Box>
            <Box sx={{ flex: '1 1 160px' }}>
              <Field label="Contact Number 2" name="ContactNumber2" value={formData.ContactNumber2}
                onChange={handleChange} maxLength={15} pattern={phonePattern}
                startIcon={<PhoneIcon sx={{ fontSize: 13, color: C.muted }} />} />
            </Box>
            <Box sx={{ flex: '1 1 200px' }}>
              <Field label="Email" name="Email" value={formData.Email}
                onChange={handleChange} maxLength={30} pattern={emailPattern}
                startIcon={<EmailIcon sx={{ fontSize: 13, color: C.muted }} />} />
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <Field label="GST Number" name="GSTNumber" value={formData.GSTNumber}
                onChange={handleChange} maxLength={15} />
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <Field label="PAN Number" name="PanNumber" value={formData.PanNumber}
                onChange={handleChange} maxLength={15} />
            </Box>
          </Section>

          {/* ── Address ── */}
          <Section title="Address" icon={<LocationOnIcon />} color="#6366f1">
            <Box sx={{ flex: '1 1 220px' }}>
              <Field label="Address Line 1" name="AddressLine1" value={formData.AddressLine1}
                onChange={handleChange} required maxLength={200} />
            </Box>
            <Box sx={{ flex: '1 1 220px' }}>
              <Field label="Address Line 2" name="AddressLine2" value={formData.AddressLine2}
                onChange={handleChange} maxLength={200} />
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <Field label="Location" name="Location" value={formData.Location}
                onChange={handleChange} />
            </Box>
            <Box sx={{ flex: '1 1 100px' }}>
              <Field label="Pin Code" name="PinCode" value={formData.PinCode}
                onChange={handleChange} />
            </Box>
          </Section>

          {/* ── Financial ── */}
          <Section title="Financial" icon={<ReceiptIcon />} color="#f59e0b">
            <Box sx={{ flex: '1 1 130px' }}>
              <Field label="Cash Opening" name="CashOpening" value={formData.CashOpening}
                onChange={handleChange} align="right" />
            </Box>
            <Box sx={{ flex: '1 1 130px' }}>
              <Field label="Stock Opening" name="StockOpening" value={formData.StockOpening}
                onChange={handleChange} align="right" />
            </Box>
            <Box sx={{ flex: '1 1 130px' }}>
              <Field label="Freight GST Rate" name="FreightGSTRate" value={formData.FreightGSTRate}
                onChange={handleChange} align="right" />
            </Box>
          </Section>

          {/* ── Super Admin sections ── */}
          {isSuperAdmin && (
            <>
              {/* UPI & Prefixes */}
              <Section title="UPI & Invoice Prefixes" icon={<QrCode2Icon />} color="#ec4899">
                <Box sx={{ flex: '1 1 160px' }}>
                  <Field label="Payment UPI ID" name="PaymentUPIId" value={formData.PaymentUPIId}
                    onChange={handleChange} />
                </Box>
                <Box sx={{ flex: '1 1 160px' }}>
                  <Field label="Payment UPI Name" name="PaymentUPIName" value={formData.PaymentUPIName}
                    onChange={handleChange} />
                </Box>
                <Box sx={{ flex: '1 1 110px' }}>
                  <Field label="B2B Prefix" name="BToBPrefix" value={formData.BToBPrefix}
                    onChange={handleChange} />
                </Box>
                <Box sx={{ flex: '1 1 110px' }}>
                  <Field label="B2C Prefix" name="BToCPrefix" value={formData.BToCPrefix}
                    onChange={handleChange} />
                </Box>
                <Box sx={{ flex: '1 1 110px' }}>
                  <Field label="IGST Prefix" name="IGSTPrefix" value={formData.IGSTPrefix}
                    onChange={handleChange} />
                </Box>
                <Box sx={{ flex: '1 1 110px' }}>
                  <Field label="IGST Customer Prefix" name="IGSTCustomerPrefix" value={formData.IGSTCustomerPrefix}
                    onChange={handleChange} />
                </Box>
              </Section>

              {/* E-Invoice */}
              <Section title="E-Invoice & E-Way Bill" icon={<LabelIcon />} color="#8b5cf6">
                <ToggleField label="E-Invoice" name="IsEinvoice" checked={formData.IsEinvoice} onChange={handleChange} />
                <ToggleField label="E-Way Bill" name="IsEwayBill" checked={formData.IsEwayBill} onChange={handleChange} />
                <Box sx={{ flex: '1 1 200px' }}>
                  <Field label="E-Invoice Portal Username" name="EinvoicePortalUserName"
                    value={formData.EinvoicePortalUserName} onChange={handleChange} />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Field label="E-Invoice Portal Password" name="EinvoicePortalPassword"
                    value={formData.EinvoicePortalPassword} onChange={handleChange} type="password" />
                </Box>
              </Section>

              {/* GST Cafe */}
              <Section title="myGSTcafe Integration" icon={<CloudIcon />} color="#0ea5e9">
                <Box sx={{ flex: '1 1 180px' }}>
                  <Field label="Customer ID" name="myGSTcafeCustomerID" value={formData.myGSTcafeCustomerID}
                    onChange={handleChange} />
                </Box>
                <Box sx={{ flex: '1 1 180px' }}>
                  <Field label="API ID" name="myGSTcafeApiID" value={formData.myGSTcafeApiID}
                    onChange={handleChange} />
                </Box>
                <Box sx={{ flex: '1 1 180px' }}>
                  <Field label="API Secret" name="myGSTcafeApiSecret" value={formData.myGSTcafeApiSecret}
                    onChange={handleChange} type="password" />
                </Box>
              </Section>
            </>
          )}

        </Box>
      </form>
    </Box>
  );
};

export default CompanyEdit;

